import { Box, Tabs, Typography, useTheme } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import type { FC } from 'react';
import { deviceIsCharger } from 'utils/common/deviceTypeUtils';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import CustomTab from 'components/MuiCustom/Tab';

import { useAppDispatch, useAppSelector } from 'redux-store';
import getAllRfidsThunk from 'redux-store/thunks/rfids/getAllRfidsThunk';
import pairDevicesToRfidsThunk from 'redux-store/thunks/rfids/pairDevicesToRfidsThunk';

import type { AssignDevicesToMembersModalStageProps } from '..';
import PairDeviceGroup from './PairDeviceGroup';
import PairDevices from './PairDevices';

interface TabButton {
  index: number;
  title: string;
}

const TabButtons: Record<'devices' | 'groups', TabButton> = {
  devices: {
    index: 0,
    title: 'devices',
  },
  groups: {
    index: 1,
    title: 'groups',
  },
};

const PairDevicesToRfids: FC<AssignDevicesToMembersModalStageProps> = ({
  state,
  selectedRfids,
  pairedDevices,
  setState,
  onCloseProxy,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const deviceGroups = useAppSelector(s => s.deviceGroups.items);
  const deviceList = useAppSelector(s => s.devices.items);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [currentTab, setCurrentTab] =
    useState<keyof typeof TabButtons>('devices');

  const filteredGroups = useMemo(
    () => deviceGroups.filter(group => groupIds.includes(group.id)),
    [deviceGroups, groupIds],
  );

  const uniqueDevices = useMemo(
    () => [
      ...new Set(
        filteredGroups.flatMap(group => group.devices ?? []).filter(Boolean),
      ),
    ],
    [filteredGroups],
  );

  const chargerIds = useMemo(
    () =>
      new Set(
        deviceList
          .filter(device => deviceIsCharger(device.type))
          .map(charger => charger.id),
      ),
    [deviceList],
  );

  const uniqueChargers = uniqueDevices.filter(id => chargerIds.has(id));

  const { pairDevices: selectedDeviceIds } = state;

  const setSelectedDeviceIds = useCallback(
    (pairDevices: number[]) => {
      setState(prev => ({ ...prev, pairDevices }));
    },
    [setState],
  );

  const setCurrentTabKey = useCallback(
    (tabIndex: number): void => {
      const tabEntry = Object.entries(TabButtons).find(
        ([, { index }]) => index === tabIndex,
      );

      if (tabEntry) {
        setCurrentTab(tabEntry[0] as keyof typeof TabButtons);
      }

      setState(prev => ({ ...prev, pairDevices: [] }));
      setGroupIds([]);
    },
    [setState],
  );

  const handlePairDevicesToRfids = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      try {
        setLoading(true);
        setError(null);

        const deviceIds =
          currentTab === 'devices' ? selectedDeviceIds : uniqueChargers;

        const result = await dispatch(
          pairDevicesToRfidsThunk({
            data: {
              rfid_ids: selectedRfids,
              device_ids: deviceIds,
            },
          }),
        ).unwrap();

        if (result.success) {
          await dispatch(getAllRfidsThunk({})).unwrap();

          setState({ pairDevices: [], stage: 'success' });

          setTimeout(() => {
            setState({ pairDevices: [], stage: 'pairDevices' });
            onClose();
          }, 1250);
        } else {
          setError(t('common:pages.rfids.pair_devices.fail'));
        }
      } catch (e) {
        Sentry.captureException(e, {
          tags: { page: 'PairDevicesToRfidsModal' },
        });
      } finally {
        setLoading(false);
      }
    },
    [
      currentTab,
      dispatch,
      onClose,
      selectedDeviceIds,
      selectedRfids,
      setState,
      t,
      uniqueChargers,
    ],
  );

  return (
    <>
      <Box
        sx={{
          height: '55vh',
          overflow: 'auto',
          paddingBottom: 5,
          width: '100%',
        }}
      >
        <Box mb={4}>
          <Typography variant="h2" mb={2} textAlign="center">
            {t('common:pages.rfids.pair_devices.pair_title')}
          </Typography>
          <Typography variant="body1" mb={1} textAlign="center">
            {t('common:pages.rfids.pair_devices.pair_description')}
          </Typography>
        </Box>
        <Box mb={4}>
          <Tabs
            value={TabButtons[currentTab].index}
            onChange={(_, v: number) => setCurrentTabKey(v)}
            sx={{
              borderBottom: `1px solid ${theme.vars.palette.divider}`,
              width: '100%',
              justifyContent: 'space-around',
            }}
          >
            {Object.entries(TabButtons).map(([key, { index }]) => (
              <CustomTab
                key={key}
                tabIndex={index}
                label={t(`common:pages.rfids.pair_devices.tabs.${key}`)}
                iconPosition="start"
                sx={{ width: '50%', ml: 0, mr: 0 }}
                disableRipple
                data-testid={key}
              />
            ))}
          </Tabs>
        </Box>
        {currentTab === 'devices' ? (
          <PairDevices
            pairedDevices={pairedDevices}
            selectedDeviceIds={selectedDeviceIds}
            setSelectedDeviceIds={setSelectedDeviceIds}
          />
        ) : (
          <PairDeviceGroup
            selectedDeviceGroupIds={groupIds}
            setSelectedDeviceGroupIds={setGroupIds}
          />
        )}
      </Box>
      <ErrorText error={error} />
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        mt={2}
        gap={2}
      >
        <Button
          variant="flat"
          color="neutral"
          size="large"
          fullWidth
          onClick={onCloseProxy}
          data-testid="modal-cancel-button"
        >
          {t('common:cancel')}
        </Button>
        <Button
          variant="flat"
          size="large"
          fullWidth
          disabled={
            isLoading || !(selectedDeviceIds.length || uniqueDevices.length)
          }
          onClick={handlePairDevicesToRfids}
          data-testid="modal-save-button"
        >
          {t('common:save')}
        </Button>
      </Box>
    </>
  );
};

export default PairDevicesToRfids;
